import Observer from "./observer";

/**
 * Undo/Redo feature for Editor.js.
 *
 * @typedef {Object} Undo
 * @description Feature's initialization class.
 * @property {Object} editor — Editor.js instance object.
 * @property {Number} maxLength - Max amount of changes recorded by the history stack.
 * @property {Function} onUpdate - Callback called when the user performs an undo or redo action.
 * @property {Boolean} shouldSaveHistory - Defines if the plugin should save the change in the stack
 * @property {Object} initialItem - Initial data object.
 */
export default class Undo {
    /**
     * @param options — Plugin custom options.
     */
    constructor({editor, config = {}, onUpdate, maxLength}) {
        const defaultOptions = {
            maxLength: 30,
            onUpdate() {
            },
            config: {
                debounceTimer: 200,
                shortcuts: {
                    undo: ["CMD+Z"],
                    redo: ["CMD+Y", "CMD+SHIFT+Z"],
                },
            },
        };

        const {blocks, caret} = editor;
        const {configuration} = editor;
        const {defaultBlock} = configuration;
        const defaultShortcuts = defaultOptions.config.shortcuts;
        const {shortcuts: configShortcuts} = config;
        const shortcuts = {...defaultShortcuts, ...configShortcuts};
        const undo = Array.isArray(shortcuts.undo) ? shortcuts.undo : [shortcuts.undo];
        const redo = Array.isArray(shortcuts.redo) ? shortcuts.redo : [shortcuts.redo];
        const defaultDebounceTimer = defaultOptions.config.debounceTimer;
        const {debounceTimer = defaultDebounceTimer} = config;
        this.editor = editor;
        this.defaultBlock = defaultBlock;
        this.blocks = blocks;
        this.caret = caret;
        this.shouldSaveHistory = true;
        this.readOnly = configuration.readOnly;
        this.maxLength = maxLength || defaultOptions.maxLength;
        this.onUpdate = onUpdate || defaultOptions.onUpdate;
        this.config = {debounceTimer, shortcuts: {undo, redo}};

        // ✨ [수정] 자식 에디터들을 관리하기 위한 Map 추가
        this.nestedEditors = new Map(); // key: blockId, value: { cols: [editorInstance, ...] }

        // ✨ [수정] 변경 감지를 에디터 컨테이너로 제한
        this.observer = new Observer(
            () => this.registerChange(),
            this.editor.configuration.holder ? document.getElementById(this.editor.configuration.holder) : document.body,
            this.config.debounceTimer
        );
        this.observer.setMutationObserver();

        this.setEventListeners();
        this.initialItem = null;
        this.clear();
    }

    /**
     * Notify core that read-only mode is suppoorted
     *
     * @returns {boolean}
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Truncates the history stack when it excedes the limit of changes.
     *
     * @param {Object} stack  Changes history stack.
     * @param {Number} stack  Limit of changes recorded by the history stack.
     */
    truncate(stack, limit) {
        while (stack.length > limit) {
            stack.shift();
        }
    }

// ✨ [추가] Columns 툴에서 자식 에디터 인스턴스를 등록하는 메소드
    /**
     * Registers a nested editor instance.
     * @param {string} blockId - The ID of the block containing the nested editor (e.g., Columns block).
     * @param {number} colIndex - The column index of the nested editor.
     * @param {object} editorInstance - The nested Editor.js instance.
     */
    registerNestedEditor(blockId, colIndex, editorInstance) {
        if (!this.nestedEditors.has(blockId)) {
            this.nestedEditors.set(blockId, {cols: []});
        }
        const blockEditors = this.nestedEditors.get(blockId);
        blockEditors.cols[colIndex] = editorInstance;
    }

// ✨ [추가] Columns 툴에서 자식 에디터 인스턴스를 제거하는 메소드
    /**
     * Unregisters nested editors for a given block ID.
     * @param {string} blockId - The ID of the block whose editors should be removed.
     */
    unregisterNestedEditors(blockId) {
        this.nestedEditors.delete(blockId);
    }

    /**
     * Initializes the stack when the user provides initial data.
     *
     * @param {Object} initialItem  Initial data provided by the user.
     */
    initialize(initialItem) {
        const initialData =
            "blocks" in initialItem ? initialItem.blocks : initialItem;
        const initialPath = {
            scope: "root",
            index: initialData.length - 1
        };

        // ▼ state 와 path 두 가지를 모두 저장
        const firstElement = {
            state: initialData,
            path: initialPath
        };

        this.stack[0] = firstElement;
        this.initialItem = firstElement;
        this.position = 0;
    }

    /**
     * Clears the history stack.
     */
    clear() {
        this.stack = this.initialItem
            ? [this.initialItem]
            : [{index: 0, state: [{type: this.defaultBlock, data: {}}]}];
        this.position = 0;
        this.onUpdate();
    }

    /**
     * Registers the data returned by API's save method into the history stack.
     */
    registerChange() {
        if (this.editor && this.editor.isReady) {
            this.editor.isReady.then(() => {
                this.editor.save().then((savedData) => {
                    const newState = savedData.blocks;
                    const newPath = this.determineChangePath();

                    const last     = this.stack[this.position];

                    const stateChanged = JSON.stringify(last.state) !== JSON.stringify(newState);


                    if (stateChanged) {
                        this.save(newState, newPath);
                    }
                });
            })
        }
    }

// ✨ [추가] 변경이 어디서 발생했는지 추적하는 로직
    determineChangePath() {
        let activeElement = document.activeElement;
        if (!activeElement) return {scope: 'root', index: this.blocks.getCurrentBlockIndex()};

        const blockElement = activeElement.closest('.ce-block');
        if (!blockElement) return {scope: 'root', index: this.blocks.getCurrentBlockIndex()};

        const columnWrapper = activeElement.closest('.ce-columns__col');

        if (columnWrapper) {
            const columnsBlock = columnWrapper.closest('.ce-block');
            const blockId = columnsBlock.dataset.id;
            const editorsInfo = this.nestedEditors.get(blockId);
            if (editorsInfo) {
                const colIndex = Array.from(columnWrapper.parentElement.children).indexOf(columnWrapper);
                const nestedEditor = editorsInfo.cols[colIndex];
                if (nestedEditor) {
                    return {
                        scope: 'nested',
                        blockId: blockId,
                        colIndex: colIndex,
                        index: nestedEditor.blocks.getCurrentBlockIndex(),
                    };
                }
            }
        }

        return {scope: 'root', index: this.blocks.getCurrentBlockIndex()};
    }

    /**
     * Adds the saved data in the history stack and updates current position.
     */
    save(state, path) {
        if (this.position >= this.maxLength) {
            this.truncate(this.stack, this.maxLength);
        }

        const snapshot = structuredClone(state);
        this.position = Math.min(this.position, this.stack.length - 1);

        this.stack = this.stack.slice(0, this.position + 1);

// ✨ [수정] 상태와 함께 경로(path)를 저장합니다.
        this.stack.push({state: snapshot, path});
        this.position += 1;
        this.onUpdate();
    }

    /**
     * Decreases the current position and update the respective block in the editor.
     */
    async undo() {
        if (!this.canUndo()) return;

        this.position -= 1;

        const {state, path} = this.stack[this.position];
        this.onUpdate();

        await this.blocks.render({blocks: state});
        await this.scrollToCaret(path);
        await this.restoreCaret(path);
    }

    /**
     * Increases the current position and update the respective block in the editor.
     */
    async redo() {
        if (!this.canRedo()) return;

        this.position += 1;

        const {state, path} = this.stack[this.position];
        this.onUpdate();

        await this.blocks.render({blocks: state});
        await this.scrollToCaret(path);
        await this.restoreCaret(path);
    }

    async scrollToCaret(path) {
        const {blockId, colIndex, index, scope} = path;

        if (scope === 'nested') {
            const editorsInfo = this.nestedEditors.get(blockId);
            if (editorsInfo && editorsInfo.cols[colIndex]) {
                const nestedEditor = editorsInfo.cols[colIndex];
                // 중첩 에디터의 블록 수가 유효한지 확인
                nestedEditor.caret.setToBlock(index, "end");
                const blockEl = nestedEditor.blocks
                    .getBlockByIndex(index)?.holder;
                if (blockEl) {
                    blockEl.scrollIntoView({
                        behavior: "instant",
                        block: "center"
                    });
                }

            }
        } else {
            const blockEl = this.blocks
                .getBlockByIndex(index)?.holder;
            if (blockEl) {
                blockEl.scrollIntoView({
                    behavior: "instant",
                    block: "center"
                });
            }
        }
    }

// ✨ [추가/개선] 경로에 따라 정확한 위치에 커서를 복원하는 메소드
    async restoreCaret(path) {
        // 렌더링 후 DOM이 업데이트될 시간을 줍니다.
        await new Promise(resolve => setTimeout(resolve, 50));

        if (path.scope === 'nested') {
            const {blockId, colIndex, index} = path;
            const editorsInfo = this.nestedEditors.get(blockId);
            if (editorsInfo && editorsInfo.cols[colIndex]) {
                const nestedEditor = editorsInfo.cols[colIndex];
                // 중첩 에디터의 블록 수가 유효한지 확인
                nestedEditor.caret.setToBlock(index, "end");

                return;
            }

            const parentIndex = this.blocks.getBlockIndex(blockId);
            if (parentIndex > -1) {
                this.caret.setToBlock(parentIndex, 'end');
            }

        } else { // scope === 'root'
            const { index } = path;
            this.caret.setToBlock(index, "end");
        }
    }

    /**
     * Checks if the history stack can perform an undo action.
     *
     * @returns {Boolean}
     */
    canUndo() {
        return !this.readOnly && this.position > 1;
    }

    /**
     * Checks if the history stack can perform a redo action.
     *
     * @returns {Boolean}
     */
    canRedo() {
        return !this.readOnly && this.position < this.count();
    }

    /**
     * Returns the number of changes recorded in the history stack.
     *
     * @returns {Number}
     */
    count() {
        return this.stack.length - 1; // -1 because of initial item
    }

    /**
     * Parses the keys passed in the shortcut property to accept CMD,ALT and SHIFT
     *
     * @param {Array} keys are the keys passed in shortcuts in config
     * @returns {Array}
     */

    parseKeys(keys) {
        const specialKeys = {
            CMD: /(Mac)/i.test(navigator.platform) ? "metaKey" : "ctrlKey",
            ALT: "altKey",
            SHIFT: "shiftKey",
        };
        const parsedKeys = keys.slice(0, -1).map((key) => specialKeys[key]);

        const letterKey =
            parsedKeys.includes("shiftKey") && keys.length === 2
                ? keys[keys.length - 1].toUpperCase()
                : keys[keys.length - 1].toLowerCase();

        parsedKeys.push(letterKey);
        return parsedKeys;
    }

    /**
     * Sets events listeners to allow keyboard actions support
     */

    setEventListeners() {
        const target = this.editor.configuration.holder ? document.getElementById(this.editor.configuration.holder) : document.body;
        const {shortcuts} = this.config;
        const {undo, redo} = shortcuts;
        const keysUndo = undo.map((undoShortcut) => undoShortcut.replace(/ /g, "").split("+"));
        const keysRedo = redo.map((redoShortcut) => redoShortcut.replace(/ /g, "").split("+"));

        const keysUndoParsed = keysUndo.map((keys) => this.parseKeys(keys));
        const keysRedoParsed = keysRedo.map((keys) => this.parseKeys(keys));

        const twoKeysPressed = (e, keys) =>
            keys.length === 2 && e[keys[0]] && (e.key.toLowerCase() === keys[1]);
        const threeKeysPressed = (e, keys) =>
            keys.length === 3 && e[keys[0]] && e[keys[1]] && (e.key.toLowerCase() === keys[2]);

        const verifyListTwoKeysPressed = (e, keysList) =>
            keysList.reduce((result, keys) => result || twoKeysPressed(e, keys), false);
        const verifyListThreeKeysPressed = (e, keysList) =>
            keysList.reduce((result, keys) => result || threeKeysPressed(e, keys), false);

        const pressedKeys = (e, keys, compKeys) => {
            if (verifyListTwoKeysPressed(e, keys) && !verifyListThreeKeysPressed(e, compKeys)) {
                return true;
            }
            if (verifyListThreeKeysPressed(e, keys)) {
                return true;
            }
            return false;
        };

        const handleUndo = (e) => {
            if (pressedKeys(e, keysUndoParsed, keysRedoParsed)) {
                e.preventDefault();
                this.undo();
            }
        };

        const handleRedo = (e) => {
            if (pressedKeys(e, keysRedoParsed, keysUndoParsed)) {
                e.preventDefault();
                this.redo();
            }
        };

        const handleDestroy = () => {
            target.removeEventListener("keydown", handleUndo);
            target.removeEventListener("keydown", handleRedo);
        };

        target.addEventListener("keydown", handleUndo);
        target.addEventListener("keydown", handleRedo);
        target.addEventListener("destroy", handleDestroy);
    }
}